import { Component } from '@theme/component';
import { onDocumentReady, changeMetaThemeColor } from '@theme/utilities';

/**
 * Update CSS custom properties for menu positioning
 */
function updateMenuPositioning() {
  const header = document.querySelector('#header-component');
  if (!header) return;
  
  const headerRect = header.getBoundingClientRect();
  const headerBottom = headerRect.bottom;
  document.body.style.setProperty('--header-bottom-position', `${headerBottom}px`);
}

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Minimum scroll distance (in px) required before a direction change is registered.
   * Prevents natural scroll jitter (trackpad momentum, mouse wheel micro-reversals) from
   * repeatedly toggling the sticky header's visibility.
   * @constant {number}
   */
  #scrollThreshold = 10;

  #syncHeaderMetrics = () => {
    const { height } = this.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);
    updateMenuPositioning();
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    this.#syncHeaderMetrics();

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Get the effective sticky mode based on current viewport
   * @returns {string | null} The sticky mode to use
   */
  #getEffectiveStickyMode = () => {
    const isMobile = window.innerWidth < 768; // Tailwind's md breakpoint
    const stickyMode = this.getAttribute('sticky');
    const stickyMobileMode = this.getAttribute('sticky-mobile');
    
    if (isMobile) {
      // On mobile, if sticky-mobile is explicitly set to "never", return null
      if (stickyMobileMode === 'never') return null;
      // If sticky-mobile has a value (always/scroll-up), use it; otherwise use desktop sticky
      return stickyMobileMode || stickyMode;
    } else {
      // On desktop, always use desktop sticky mode
      return stickyMode;
    }
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
        // Update menu positioning when sticky state changes
        updateMenuPositioning();
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const effectiveStickyMode = this.#getEffectiveStickyMode();
    
    // Exit early if no sticky mode is active
    if (!effectiveStickyMode) return;
    
    if (!this.#offscreen && effectiveStickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const scrollDelta = scrollTop - this.#lastScrollTop;

    // Ignore tiny scroll movements so natural scroll jitter doesn't flip the
    // scroll direction and cause the header to flicker/disappear
    if (Math.abs(scrollDelta) < this.#scrollThreshold) return;

    const isScrollingUp = scrollDelta < 0;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (effectiveStickyMode === 'always') {
      if (isScrollingUp) {
        if (this.getBoundingClientRect().top >= 0) {
          this.dataset.scrollDirection = 'none';
        } else {
          this.dataset.scrollDirection = 'up';
        }
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      if (document.body.classList.contains('desktop-drawer-open')) {
        return;
      }

      const drawerClosedAt = this._drawerClosedAt || 0;
      const timeSinceDrawerClosed = Date.now() - drawerClosedAt;
      if (timeSinceDrawerClosed < 500) {
        return;
      }

      this.setAttribute('data-animating', '');
      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      if (!document.body.classList.contains('desktop-drawer-open')) {
        this.dataset.stickyState = 'idle';
      }
    }

    this.#lastScrollTop = scrollTop;
    
    // Update menu positioning after scroll changes
    updateMenuPositioning();
  };

  /**
   * Handle window resize to re-evaluate sticky behavior
   */
  #handleResize = () => {    this.#syncHeaderMetrics();
    requestAnimationFrame(this.#syncHeaderMetrics);

    // Disconnect existing observer if it exists
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    // Remove existing scroll listener
    document.removeEventListener('scroll', this.#handleWindowScroll);

    // Re-initialize with new effective sticky mode
    this.#initializeStickyBehavior();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.#syncHeaderMetrics();
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    this.#initializeStickyBehavior();

    // Listen for window resize to re-evaluate sticky mode
    window.addEventListener('resize', this.#handleResize);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleEditorSelect);
      document.addEventListener('shopify:section:select', this.#handleEditorSelect);
    }
  }

  /**
   * Initialize sticky behavior based on current effective mode
   */
  #initializeStickyBehavior = () => {
    const effectiveStickyMode = this.#getEffectiveStickyMode();
    if (effectiveStickyMode) {
      this.#observeStickyPosition(effectiveStickyMode === 'always');

      if (effectiveStickyMode === 'scroll-up' || effectiveStickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  };

  /**
   * Theme Editor: keep the header shown/active while one of its nested blocks is being edited.
   * @param {Event} event
   */
  #handleEditorSelect = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    this.removeAttribute('data-animating');
    this.dataset.stickyState = 'active';
    this.dataset.scrollDirection = 'up';
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    window.removeEventListener('resize', this.#handleResize);
    document.removeEventListener('shopify:block:select', this.#handleEditorSelect);
    document.removeEventListener('shopify:section:select', this.#handleEditorSelect);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentReady(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
